import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, AD_KIND_LABEL, resolveUrl, type AdDate, type AdItem, type AdKind, type EditableMark, type Mark, type MarkAd, type MarkGroup, type MarkLodging } from '../api';
import { ChevronLeft, Megaphone, Plus, Trash2 } from 'lucide-react';
import { RADIUS, TYPE, getTheme, useThemeMode } from '../theme';
import { Badge, Button, Card, Chip, EmptyState, Icon, IconButton, Input, SectionHeader, Select, Textarea } from '../components/ui';
import { parseSatar } from '../karta/satar';
import { ImagePickerModal } from '../modals/ImagePickerModal';

const kr = (n: number) => `${Math.round(n).toLocaleString('sv-SE')} kr`;
export const adPriceText = (ad: Pick<MarkAd, 'priceAmount' | 'priceUnit'>) =>
  ad.priceAmount > 0 ? `${kr(ad.priceAmount)} / ${ad.priceUnit}` : 'Pris saknas';

const fmtDay = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
export const datesText = (dates: AdDate[]) => dates.map(d => (d.start === d.end ? fmtDay(d.start) : `${fmtDay(d.start)}–${fmtDay(d.end)}`)).join(', ');

const KINDS: { k: AdKind; d: string }[] = [
  { k: 'jaktlag', d: 'Plats i jaktlaget på marken' },
  { k: 'jaktklubb', d: 'Medlemskap i en jaktklubbsgrupp' },
  { k: 'enskild', d: 'Ett eller flera datum, eller en period' },
  { k: 'jaktpaket', d: 'Jaktdagar och jaktklubbar, även över flera marker' },
];

/**
 * Markens annonser. Annonsen bestämmer typ, rubrik, text, datum och prisgrupp — priser och
 * markuppgifter (viltförekomst, faciliteter, bilder) hämtas alltid från marken.
 */
/** Standardval för "Använd även som markens bild": marken saknar bild, eller annonsen är markens enda/huvudsakliga annons. */
export function defaultUseAsMarkImage(mark: Pick<Mark, 'id' | 'imageUrl'>, ad: Pick<MarkAd, 'id' | 'kind' | 'markId'> | null, ads: readonly Pick<MarkAd, 'id' | 'kind' | 'markId'>[]): boolean {
  if (!mark.imageUrl) return true;
  const others = ads.filter(a => a.markId === mark.id && a.id !== ad?.id);
  if (!others.length) return true;
  return (ad?.kind ?? 'jaktlag') === 'jaktlag' && !others.some(a => a.kind === 'jaktlag');
}

export function AdsTab({ mark, groups, onOpenEkonomi, onOpenMarkInfo, openAdId, onMarkImageChanged }: {
  mark: Mark; groups: MarkGroup[];
  /** Markens bild har ändrats via en annons (B11) – uppdatera markens state och Min jakt. */
  onMarkImageChanged?: (m: Mark) => void;
  onOpenEkonomi: (groupId?: string | null) => void;
  onOpenMarkInfo: () => void;
  openAdId?: string | null;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const [ads, setAds] = useState<MarkAd[] | null>(null);
  const [err, setErr] = useState('');
  const [editing, setEditing] = useState<MarkAd | 'new' | null>(null);

  const load = () => api.markAds(mark.id).then(list => {
    setAds(list);
    if (openAdId) { const a = list.find(x => x.id === openAdId); if (a) setEditing(a); }
  }).catch(e => setErr(e instanceof Error ? e.message : 'Kunde inte hämta annonserna'));
  useEffect(() => { load(); }, [mark.id]); // eslint-disable-line

  if (editing) {
    return <AdEditor mark={mark} groups={groups} ad={editing === 'new' ? null : editing} siblingAds={ads ?? []} onMarkImageChanged={onMarkImageChanged}
      onOpenEkonomi={onOpenEkonomi} onOpenMarkInfo={onOpenMarkInfo}
      onClose={() => { setEditing(null); load(); }}
      onSaved={a => { setEditing(a); load(); }} />;
  }

  return (
    <div>
      <SectionHeader title="Annonser" action={ads && ads.length > 0 && <Button pill icon={<Icon as={Plus} size={18} />} onClick={() => setEditing('new')}>Ny annons</Button>} />
      <div style={{ ...TYPE.small, fontSize: 14, color: T.textSub, margin: '-6px 0 16px' }}>Annonserna byggs av markens priser och uppgifter. Ändra priser under Ekonomi.</div>
      {!mark.isPublished && <div style={{ padding: '12px 14px', borderRadius: RADIUS.md, background: T.accentSoft, color: T.text, fontSize: 14, marginBottom: 14 }}>Marken är inte publicerad än. Annonserna syns på marknaden när Qvenari har godkänt marken.</div>}
      {err && <div role="alert" style={{ color: T.danger, fontSize: 14, fontWeight: 600 }}>{err}</div>}
      {ads && ads.length === 0 && (
        <Card><EmptyState icon={Megaphone} title="Inga annonser ännu" sub="Annonsera plats i jaktlaget, medlemskap i jaktklubben, enskilda jakter eller jaktpaket."
          action={<Button icon={<Icon as={Plus} size={18} />} onClick={() => setEditing('new')}>Ny annons</Button>} /></Card>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ads?.map(a => (
          <button key={a.id} onClick={() => setEditing(a)} style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '14px 16px', minHeight: 64, borderRadius: RADIUS.lg, border: `1px solid ${T.border}`, background: T.surface, cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', color: T.text }}>
            <Badge tone="accent">{AD_KIND_LABEL[a.kind]}</Badge>
            <span style={{ flex: '1 1 200px', minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 16, fontWeight: 700 }}>{a.title}</span>
              <span style={{ display: 'block', fontSize: 13, color: T.textMuted, marginTop: 2 }}>
                {adPriceText(a)}{a.groupName ? ` · ${a.groupName}` : ''}{a.dates.length ? ` · ${datesText(a.dates)}` : ''}{a.satNames?.length ? ` · ${a.satNames.join(', ')}` : ''}{a.markId !== mark.id ? ` · huvudmark ${a.markName}` : ''}
              </span>
            </span>
            <Badge tone={a.status === 'published' ? 'ok' : 'neutral'} dot>{a.status === 'published' ? 'Publicerad' : 'Utkast'}</Badge>
          </button>
        ))}
      </div>
    </div>
  );
}

function AdEditor({ mark, groups, ad, siblingAds, onMarkImageChanged, onOpenEkonomi, onOpenMarkInfo, onClose, onSaved }: {
  mark: Mark; groups: MarkGroup[]; ad: MarkAd | null; siblingAds: MarkAd[]; onMarkImageChanged?: (m: Mark) => void;
  onOpenEkonomi: (groupId?: string | null) => void; onOpenMarkInfo: () => void;
  onClose: () => void; onSaved: (a: MarkAd) => void;
}) {
  const { mode } = useThemeMode();
  const T = getTheme(mode);
  const jaktklubbGroup = groups.find(g => g.jaktklubb?.isJaktklubb) ?? groups.find(g => g.name.toLowerCase() === 'jaktklubb');
  const [kind, setKind] = useState<AdKind>(ad?.kind ?? 'jaktlag');
  const [title, setTitle] = useState(ad?.title ?? mark.name);
  const [description, setDescription] = useState(ad?.description ?? '');
  const [groupId, setGroupId] = useState<string | null>(ad?.groupId ?? null);
  const [dates, setDates] = useState<AdDate[]>(ad?.dates.length ? ad.dates : []);
  const [imageUrl, setImageUrl] = useState<string | null>(ad?.imageUrl ?? null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [spots, setSpots] = useState(ad?.spots != null ? String(ad.spots) : '');
  const [items, setItems] = useState<AdItem[]>(ad?.lines.map(l => ({ markId: l.markId, groupId: l.groupId, kind: l.kind, days: l.days })) ?? []);
  const [marks, setMarks] = useState<EditableMark[]>([]);
  // L1: boende som kan köpas tillsammans med jakten (enskild jakt och jaktpaket, huvudmarkens boenden).
  const [lodgingId, setLodgingId] = useState<string | null>(ad?.lodgingId ?? null);
  const [lodgings, setLodgings] = useState<MarkLodging[]>([]);
  const lodgingKind = kind === 'enskild' || kind === 'jaktpaket';
  // S1: enskild jakt kan gälla en eller flera av markens såter (ingen vald = hela marken).
  const markSatar = useMemo(() => parseSatar(mark.coordinatesJson), [mark.coordinatesJson]);
  const [satIds, setSatIds] = useState<string[]>(ad?.satIds ?? []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const published = ad?.status === 'published';
  // B11: annonsens bild kan också sparas som markens bild, så att den syns under Min jakt.
  const [markImageChoice, setMarkImageChoice] = useState<boolean | null>(null);
  const ownMark = (ad?.markId ?? mark.id) === mark.id;
  const showMarkImage = ownMark && !!imageUrl && imageUrl !== mark.imageUrl;
  const useAsMarkImage = markImageChoice ?? defaultUseAsMarkImage(mark, ad, siblingAds);

  useEffect(() => { if (kind === 'jaktpaket') api.editableMarks().then(setMarks).catch(() => {}); }, [kind]);
  useEffect(() => { if (lodgingKind) api.markLodgings(ad?.markId ?? mark.id, true).then(setLodgings).catch(() => {}); }, [lodgingKind, ad?.markId, mark.id]);
  useEffect(() => {
    // Ny jaktklubbsannons: föreslå standardgruppen Jaktklubb.
    if (!ad && kind === 'jaktklubb' && !groupId && jaktklubbGroup) setGroupId(jaktklubbGroup.id);
    if (!ad && kind === 'jaktpaket' && !items.length) setItems([{ markId: mark.id, groupId: null, kind: 'jaktdagar', days: 2 }]);
  }, [kind]); // eslint-disable-line

  const species = useMemo(() => { try { return Object.entries(JSON.parse(mark.speciesOccurrenceJson || '{}') as Record<string, number>).filter(([, v]) => v > 0); } catch { return []; } }, [mark.speciesOccurrenceJson]);
  const facilities = useMemo(() => { try { return Object.entries(JSON.parse(mark.facilitiesJson || '{}') as Record<string, number>).filter(([, v]) => v > 0); } catch { return []; } }, [mark.facilitiesJson]);

  const save = async (imageOverride?: { url: string | null }): Promise<MarkAd | null> => {
    setBusy(true); setMsg('');
    try {
      const saved = await api.saveMarkAd({
        id: ad?.id, kind, markId: ad?.markId ?? mark.id, groupId: kind === 'jaktpaket' ? null : groupId,
        title, description, imageUrl: imageOverride ? imageOverride.url : imageUrl, dates: kind === 'enskild' ? dates : [], spots: spots ? Number(spots) : null,
        items: kind === 'jaktpaket' ? items : [],
        lodgingId: lodgingKind ? lodgingId : null,
        satIds: kind === 'enskild' ? satIds.filter(id => markSatar.some(x => x.id === id)) : [],
      });
      if (showMarkImage && useAsMarkImage && !imageOverride) {
        try { onMarkImageChanged?.(await api.updateMark(mark.id, { imageUrl: imageUrl! })); }
        catch { setMsg('Annonsen sparades men markens bild kunde inte ändras'); onSaved(saved); return saved; }
      }
      onSaved(saved);
      setMsg('Sparat');
      return saved;
    } catch (e) { setMsg(e instanceof Error ? e.message : 'Kunde inte spara'); return null; }
    finally { setBusy(false); }
  };
  // B2: en borttagen bild sparas direkt på en befintlig annons, så den är borta efter omladdning och på den publika annonsen.
  const removeImage = async () => {
    const prev = imageUrl;
    setImageUrl(null);
    if (!ad) return;
    const saved = await save({ url: null });
    if (!saved) setImageUrl(prev);
  };
  const upload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true); setMsg('');
    try { const res = await api.uploadFile(file); setImageUrl(res.url); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Kunde inte ladda upp bilden'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const publish = async (p: boolean) => {
    const saved = p ? await save() : ad;
    if (!saved) return;
    setBusy(true);
    try { onSaved(await api.publishMarkAd(saved.id, p)); setMsg(p ? 'Publicerad' : 'Avpublicerad'); }
    catch (e) { setMsg(e instanceof Error ? e.message : 'Kunde inte publicera'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!ad || !confirm(`Ta bort annonsen "${ad.title}"?`)) return;
    setBusy(true);
    try { await api.deleteMarkAd(ad.id); onClose(); } catch (e) { setMsg(e instanceof Error ? e.message : 'Kunde inte ta bort'); setBusy(false); }
  };

  const label: React.CSSProperties = { display: 'block', color: T.text, fontSize: 14, fontWeight: 700, marginBottom: 6 };
  const card: React.CSSProperties = { marginBottom: 14 };
  const linkBtn: React.CSSProperties = { background: 'none', border: 'none', color: T.accentText, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', padding: '0 4px', minHeight: 44 };
  const groupOptions = (gs: MarkGroup[], allowBase: boolean) => (
    <>
      {allowBase && <option value="">Grundpris (ingen grupp)</option>}
      {gs.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
    </>
  );

  return (
    <div style={{ maxWidth: 820 }}>
      <Button variant="ghost" icon={<Icon as={ChevronLeft} size={18} />} onClick={onClose} style={{ marginBottom: 8, marginLeft: -12, color: T.textSub }}>Alla annonser</Button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <h2 style={{ ...TYPE.section, margin: 0, color: T.text }}>{ad ? 'Redigera annons' : 'Ny annons'}</h2>
        {ad && <Badge tone={published ? 'ok' : 'neutral'} dot>{published ? 'Publicerad' : 'Utkast'}</Badge>}
      </div>

      <Card style={card}>
        <span style={label}>Typ av annons</span>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 8 }}>
          {KINDS.map(({ k, d }) => (
            <button key={k} type="button" aria-pressed={kind === k} disabled={published && k !== kind} onClick={() => setKind(k)} style={{ textAlign: 'left', padding: 12, minHeight: 64, borderRadius: RADIUS.md, cursor: published ? 'default' : 'pointer', fontFamily: 'inherit', border: `${kind === k ? 2 : 1}px solid ${kind === k ? T.accentFill : T.border}`, background: kind === k ? T.accentSoft : T.surface, color: T.text, opacity: published && k !== kind ? 0.45 : 1 }}>
              <span style={{ display: 'block', fontWeight: 800, fontSize: 15 }}>{AD_KIND_LABEL[k]}</span>
              <span style={{ display: 'block', fontSize: 13, color: T.textMuted, marginTop: 2 }}>{d}</span>
            </button>
          ))}
        </div>
      </Card>

      <Card style={card}>
        <label style={{ display: 'block', marginBottom: 12 }}><span style={label}>Rubrik</span><Input value={title} onChange={e => setTitle(e.target.value)} /></label>
        <label style={{ display: 'block' }}><span style={label}>Annonstext (valfritt – markens beskrivning visas också)</span><Textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} /></label>
      </Card>

      <Card style={card}>
        <span style={label}>Annonsbild (valfritt – annars visas markens bild)</span>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 140, aspectRatio: '4 / 3', borderRadius: RADIUS.md, overflow: 'hidden', background: T.surface2, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.textMuted, fontSize: 13, textAlign: 'center' }}>
            {imageUrl ? <img src={resolveUrl(imageUrl)} alt="Annonsbild" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : mark.imageUrl ? <img src={resolveUrl(mark.imageUrl)} alt="Markens bild" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.75 }} /> : 'Markens bild'}
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <button type="button" style={linkBtn} onClick={() => setPickerOpen(true)}>Markens album</button>
            <button type="button" style={linkBtn} disabled={uploading} onClick={() => fileRef.current?.click()}>{uploading ? 'Laddar upp…' : 'Ladda upp ny bild'}</button>
            {imageUrl && <button type="button" style={{ ...linkBtn, color: T.danger }} disabled={busy} onClick={removeImage}>Ta bort bild</button>}
            {!imageUrl && <span style={{ fontSize: 13, color: T.textMuted }}>Ingen egen bild – markens bild visas.</span>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" aria-label="Ladda upp annonsbild" style={{ display: 'none' }} onChange={e => upload(e.target.files?.[0])} />
        </div>
        {showMarkImage && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, marginTop: 10, color: T.text, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            <input type="checkbox" checked={useAsMarkImage} onChange={e => setMarkImageChoice(e.target.checked)} style={{ width: 20, height: 20, accentColor: T.accentFill, flexShrink: 0 }} />
            Använd även som markens bild
          </label>
        )}
        {pickerOpen && <ImagePickerModal markId={ad?.markId ?? mark.id} onPick={urls => { setImageUrl(urls[0]); setPickerOpen(false); }} onUpload={() => { setPickerOpen(false); fileRef.current?.click(); }} onClose={() => setPickerOpen(false)} />}
      </Card>

      {kind !== 'jaktpaket' && (
        <Card style={card}>
          <span style={label}>Prisgrupp</span>
          <Select value={groupId ?? ''} onChange={e => setGroupId(e.target.value || null)}>{groupOptions(groups, kind !== 'jaktklubb')}</Select>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 6 }}>
            {kind === 'jaktklubb' ? 'De som söker och godkänns blir medlemmar i gruppen och betalar gruppens priser.' : 'Annonsen visar gruppens priser från Ekonomi.'}
          </div>
        </Card>
      )}

      {kind === 'enskild' && (
        <Card style={card}>
          <span style={label}>Datum</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {dates.map((d, i) => {
              const period = d.end !== d.start;
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Select value={period ? 'period' : 'datum'} onChange={e => setDates(l => l.map((x, j) => (j === i ? { start: x.start, end: e.target.value === 'period' ? addDays(x.start, 1) : x.start } : x)))} style={{ width: 120 }}>
                    <option value="datum">Datum</option><option value="period">Period</option>
                  </Select>
                  <Input type="date" value={d.start} onChange={e => setDates(l => l.map((x, j) => (j === i ? { start: e.target.value, end: period ? (x.end < e.target.value ? e.target.value : x.end) : e.target.value } : x)))} style={{ width: 170 }} aria-label="Startdatum" />
                  {period && <><span style={{ color: T.textSub }}>till</span><Input type="date" value={d.end} min={d.start} onChange={e => setDates(l => l.map((x, j) => (j === i ? { ...x, end: e.target.value } : x)))} style={{ width: 170 }} aria-label="Slutdatum" /></>}
                  <IconButton label="Ta bort datum" onClick={() => setDates(l => l.filter((_, j) => j !== i))} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>
                </div>
              );
            })}
            <button type="button" style={{ ...linkBtn, alignSelf: 'flex-start' }} onClick={() => setDates(l => [...l, { start: l.length ? addDays(l[l.length - 1].end, 7) : today(), end: l.length ? addDays(l[l.length - 1].end, 7) : today() }])}>+ Lägg till datum eller period</button>
          </div>
          <label style={{ display: 'block', marginTop: 12, maxWidth: 200 }}><span style={label}>Antal platser</span><Input inputMode="numeric" value={spots} onChange={e => setSpots(e.target.value.replace(/\D/g, ''))} /></label>
        </Card>
      )}

      {kind === 'jaktpaket' && (
        <Card style={card}>
          <span style={label}>Paketets delar</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {items.map((it, i) => {
              const m = marks.find(x => x.id === it.markId);
              const gs = m?.groups ?? (it.markId === mark.id ? groups : []);
              return (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 8, alignItems: 'center', paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
                  <Select value={it.markId} onChange={e => setItems(l => l.map((x, j) => (j === i ? { ...x, markId: e.target.value, groupId: null } : x)))} aria-label="Mark">
                    {!marks.some(x => x.id === it.markId) && <option value={it.markId}>{it.markId === mark.id ? mark.name : 'Mark'}</option>}
                    {marks.map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
                  </Select>
                  <Select value={it.kind} onChange={e => setItems(l => l.map((x, j) => (j === i ? { ...x, kind: e.target.value as AdItem['kind'], days: e.target.value === 'jaktdagar' ? Math.max(1, x.days) : 0 } : x)))} aria-label="Del">
                    <option value="jaktdagar">Jaktdagar</option><option value="jaktklubb">Jaktklubb</option>
                  </Select>
                  <Select value={it.groupId ?? ''} onChange={e => setItems(l => l.map((x, j) => (j === i ? { ...x, groupId: e.target.value || null } : x)))} aria-label="Prisgrupp">{groupOptions(gs, it.kind === 'jaktdagar')}</Select>
                  {it.kind === 'jaktdagar'
                    ? <Input inputMode="numeric" value={String(it.days)} onChange={e => setItems(l => l.map((x, j) => (j === i ? { ...x, days: Number(e.target.value.replace(/\D/g, '')) || 0 } : x)))} aria-label="Antal dagar" />
                    : <span style={{ fontSize: 13, color: T.textSub }}>1 år</span>}
                  <IconButton label="Ta bort del" onClick={() => setItems(l => l.filter((_, j) => j !== i))} style={{ color: T.danger }}><Icon as={Trash2} size={18} /></IconButton>
                </div>
              );
            })}
            <button type="button" style={{ ...linkBtn, alignSelf: 'flex-start' }} onClick={() => setItems(l => [...l, { markId: mark.id, groupId: null, kind: 'jaktdagar', days: 1 }])}>+ Lägg till del</button>
            <div style={{ fontSize: 13, color: T.textSub }}>Du kan ta med marker där du är ägare eller jaktledare. Paketpriset är summan av delarnas priser.</div>
          </div>
          <label style={{ display: 'block', marginTop: 12, maxWidth: 200 }}><span style={label}>Antal platser</span><Input inputMode="numeric" value={spots} onChange={e => setSpots(e.target.value.replace(/\D/g, ''))} /></label>
        </Card>
      )}

      {kind === 'enskild' && markSatar.length > 0 && (
        <Card style={card} data-testid="ad-sater">
          <span style={label}>Såter</span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {markSatar.map(s => (
              <Chip key={s.id} selected={satIds.includes(s.id)} onClick={() => setSatIds(l => l.includes(s.id) ? l.filter(x => x !== s.id) : [...l, s.id])}>{s.name}</Chip>
            ))}
          </div>
          <div style={{ fontSize: 13, color: T.textSub, marginTop: 6 }}>
            {satIds.length ? 'Jakten bokar bara de valda såterna. Andra såter kan bokas samma dag.' : 'Ingen såt vald: jakten gäller hela marken och krockar med bokningar i alla såter samma dag.'}
          </div>
        </Card>
      )}

      {lodgingKind && (
        <Card style={card} data-testid="ad-lodging">
          <span style={label}>Boende</span>
          {lodgings.length > 0 ? (
            <>
              <Select value={lodgingId ?? ''} onChange={e => setLodgingId(e.target.value || null)} aria-label="Boende i annonsen" style={{ maxWidth: 360 }}>
                <option value="">Inget boende</option>
                {lodgings.map(l => <option key={l.id} value={l.id}>{l.name}{l.active ? '' : ' (pausat)'}</option>)}
              </Select>
              <div style={{ fontSize: 13, color: T.textSub, marginTop: 6 }}>Den som köper jakten kan lägga till boendet i samma betalning. Det reserveras och dras tillsammans med jakten.</div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: T.textSub }}>Marken har inget boende. Lägg upp boende under Ekonomi → Boende för att kunna erbjuda det i annonsen.</div>
          )}
        </Card>
      )}

      {/* Från marken – skrivskyddat */}
      <Card style={{ ...card, background: T.surface2 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10, gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: T.text }}>Pris och uppgifter från marken</span>
          <span style={{ fontSize: 13, color: T.textMuted }}>Ändras inte i annonsen</span>
        </div>
        {ad ? (
          <>
            <div style={{ fontSize: 22, fontWeight: 800, color: T.text }}>{adPriceText(ad)}</div>
            {ad.lines.length > 0 && <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>{ad.lines.map(l => `${l.markName}: ${l.kind === 'jaktklubb' ? `jaktklubb${l.groupName ? ` (${l.groupName})` : ''}` : `${l.days} jaktdagar`} ${kr(l.amount)}`).join(' · ')}</div>}
            {ad.price && ad.price.fallavgifter.length > 0 && <div style={{ fontSize: 13, color: T.textSub, marginTop: 4 }}>Fällavgifter: {ad.price.fallavgifter.map(f => `${f.viltslag} ${kr(f.kostnad)}`).join(' · ')}</div>}
            {ad.price && <div style={{ fontSize: 13, color: T.textSub, marginTop: 2 }}>Kött: {ad.price.kottIngar ? 'ingår' : ad.price.kottkostnad.length ? ad.price.kottkostnad.map(k => `${k.viltslag} ${k.kostnadPerKg} kr/kg`).join(' · ') : 'enligt överenskommelse'}</div>}
          </>
        ) : <div style={{ fontSize: 14, color: T.textSub }}>Priset räknas fram när annonsen sparas.</div>}
        <div style={{ fontSize: 13, color: T.textSub, marginTop: 10 }}>
          {species.length ? `Vilt: ${species.map(([k]) => k).join(', ')}` : 'Ingen viltförekomst angiven'} · {facilities.length ? `Utrustning: ${facilities.map(([k, v]) => (v > 1 ? `${k} ×${v}` : k)).join(', ')}` : 'Ingen utrustning angiven'}
        </div>
        <div style={{ display: 'flex', gap: 18, marginTop: 12, flexWrap: 'wrap' }}>
          <button type="button" style={linkBtn} onClick={() => onOpenEkonomi(kind === 'jaktpaket' ? null : groupId)}>Ändra priser i Ekonomi →</button>
          <button type="button" style={linkBtn} onClick={onOpenMarkInfo}>Ändra markens uppgifter →</button>
        </div>
      </Card>

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="secondary" pill disabled={busy} onClick={() => save()}>{published ? 'Spara ändringar' : 'Spara utkast'}</Button>
        <Button variant={published ? 'secondary' : 'primary'} pill disabled={busy} onClick={() => publish(!published)}>{published ? 'Avpublicera' : 'Publicera'}</Button>
        {ad && <Button variant="ghost" disabled={busy} onClick={remove} style={{ color: T.danger }}>Ta bort</Button>}
        {msg && <span role="status" style={{ fontSize: 14, fontWeight: 600, color: ['Sparat', 'Publicerad', 'Avpublicerad'].includes(msg) ? T.ok : T.danger }}>{msg}</span>}
      </div>
    </div>
  );
}

function today() { return new Date().toISOString().slice(0, 10); }
function addDays(d: string, n: number) { const x = new Date(`${d}T12:00:00`); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); }
